import { useState, useEffect } from 'react';
import {
  getInterpretations,
  createInterpretation,
  updateInterpretation,
  deleteInterpretation,
} from '../services/interpretations';
import { Interpretation, InterpretationCreate, LANGUAGES, PHILOSOPHIES } from '../types/interpretation';
import { useParams } from 'react-router-dom';
import { useConfirm } from '../contexts/ConfirmDialogContext';

interface InterpretationsListProps {
  projectName: string;
  section: string;
  chapter: number;
  number: number;
}

export default function InterpretationsList({ projectName, section, chapter, number }: InterpretationsListProps) {
  console.log('InterpretationsList props:', { projectName, section, chapter, number });
  const [interpretations, setInterpretations] = useState<Interpretation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [newInterpretation, setNewInterpretation] = useState<InterpretationCreate>({
    text: '',
    language: LANGUAGES.EN,
    philosophy: PHILOSOPHIES.ADV,
  });
  const [editingInterpretation, setEditingInterpretation] = useState<Interpretation | null>(null);

  const confirm = useConfirm();
  
  useEffect(() => {
    fetchInterpretations();
  }, [projectName, section, chapter, number]);

  const fetchInterpretations = async () => {
    try {
      setLoading(true);
      console.log('Fetching interpretations with:', { projectName, section, chapter, number });
      const data = await getInterpretations(projectName, section, chapter, number);
      console.log('Fetched interpretations:', data);
      setInterpretations(data);
      setError(null);
    } catch (err) {
      console.error('Error fetching interpretations:', err);
      // If it's a 404, that's expected when there are no interpretations
      if (err instanceof Error && err.message.includes('404')) {
        setInterpretations([]);
        setError(null);
      } else {
        setError(err instanceof Error ? err.message : 'Failed to fetch interpretations');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    try {
      console.log('Creating interpretation with:', { projectName, section, chapter, number, newInterpretation });
      const created = await createInterpretation(projectName, section, chapter, number, newInterpretation);
      console.log('Created interpretation:', created);
      setInterpretations([...interpretations, created]);
      setNewInterpretation({ text: '', language: LANGUAGES.EN, philosophy: PHILOSOPHIES.ADV });
    } catch (err) {
      console.error('Error creating interpretation:', err);
      setError(err instanceof Error ? err.message : 'Failed to create interpretation');
    }
  };

  const handleUpdate = async () => {
    if (!editingInterpretation) return;
    try {
      console.log('Updating interpretation with:', { projectName, section, chapter, number, editingInterpretation });
      const updated = await updateInterpretation(projectName, section, chapter, number, editingInterpretation);
      console.log('Updated interpretation:', updated);
      setInterpretations(interpretations.map(i => i.id === updated.id ? updated : i));
      setEditingInterpretation(null);
    } catch (err) {
      console.error('Error updating interpretation:', err);
      setError(err instanceof Error ? err.message : 'Failed to update interpretation');
    }
  };

  const handleDelete = async (language: string, philosophy: string) => {
        const ok = await confirm({
            title: 'Confirm Delete',
            message: `Are you sure you want to delete ...?`,
            confirmText: 'Delete',
            cancelText: 'Cancel',
        });

        if (!ok) return;

    try {
      console.log('Deleting interpretation with:', { projectName, section, chapter, number, language, philosophy });
      await deleteInterpretation(projectName, section, chapter, number, language, philosophy);
      setInterpretations(interpretations.filter(i => !(i.language === language && i.philosophy === philosophy)));
    } catch (err) {
      console.error('Error deleting interpretation:', err);
      setError(err instanceof Error ? err.message : 'Failed to delete interpretation');
    }
  };

  return (
    <div className="p-4">
      <h2 className="text-2xl font-bold mb-4">Interpretation for Sutra {section}/{chapter}/{number} in {projectName}</h2>
      
      {/* Create new interpretation */}
      <div className="mb-6 p-4 border rounded">
        <h3 className="text-xl font-semibold mb-2">Add New Interpretation</h3>
        <div className="space-y-2">
          <textarea
            className="w-full p-2 border rounded"
            value={newInterpretation.text}
            onChange={(e) => setNewInterpretation({ ...newInterpretation, text: e.target.value })}
            placeholder="Enter interpretation text"
          />
          <select
            className="p-2 border rounded"
            value={newInterpretation.language}
            onChange={(e) => setNewInterpretation({ ...newInterpretation, language: e.target.value })}
          >
            {Object.entries(LANGUAGES).map(([key, value]) => (
              <option key={value} value={value}>{key}</option>
            ))}
          </select>
          <select
            className="p-2 border rounded"
            value={newInterpretation.philosophy}
            onChange={(e) => setNewInterpretation({ ...newInterpretation, philosophy: e.target.value })}
          >
            {Object.entries(PHILOSOPHIES).map(([key, value]) => (
              <option key={value} value={value}>{key}</option>
            ))}
          </select>
          <button
            className="bg-blue-500 text-white px-4 py-2 rounded"
            onClick={handleCreate}
          >
            Add Interpretation
          </button>
        </div>
      </div>

      {loading && <div>Loading...</div>}
      {error && <div>Error: {error}</div>}

      {/* Interpretations list */}
      <div className="space-y-4">
        {!loading && !error && interpretations.length === 0 ? (
          <div>No interpretations found for this sutra. Add a new interpretation above.</div>
        ) : (
          interpretations.map((interpretation) => (
            <div key={`${interpretation.language}-${interpretation.philosophy}`} className="border p-4 rounded">
              {editingInterpretation?.id === interpretation.id ? (
                <div className="space-y-2">
                  <textarea
                    className="w-full p-2 border rounded"
                    value={editingInterpretation.text}
                    onChange={(e) => setEditingInterpretation({ ...editingInterpretation, text: e.target.value })}
                  />
                  <div className="flex space-x-2">
                    <button
                      className="bg-green-500 text-white px-4 py-2 rounded"
                      onClick={handleUpdate}
                    >
                      Save
                    </button>
                    <button
                      className="bg-gray-500 text-white px-4 py-2 rounded"
                      onClick={() => setEditingInterpretation(null)}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div>
                  <p className="text-lg">{interpretation.text}</p>
                  <p className="text-sm text-gray-600">
                    Language: {interpretation.language} | Philosophy: {interpretation.philosophy}
                  </p>
                  <div className="mt-2 space-x-2">
                    <button
                      className="bg-yellow-500 text-white px-4 py-2 rounded"
                      onClick={() => setEditingInterpretation(interpretation)}
                    >
                      Edit
                    </button>
                    <button
                      className="bg-red-500 text-white px-4 py-2 rounded"
                      onClick={() => handleDelete(interpretation.language, interpretation.philosophy)}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
} 